import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icons } from '../../lib/Icons';
import { getSencoCommandCenterV2 } from '../../lib/functions';

const NEED_OPTIONS = [
  { value: 'ASD', label: 'ASD' },
  { value: 'LanguageAndCommunication', label: 'Language & Communication' },
  { value: 'ADHD', label: 'ADHD' },
  { value: 'Dyslexia', label: 'Dyslexia' },
  { value: 'Other', label: 'Other' },
];

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'unknown', label: 'Unknown' },
];

function needsSummaryRows(keyNeeds) {
  return [
    { key: 'ASD', label: 'ASD', count: keyNeeds?.ASD || 0 },
    {
      key: 'LanguageAndCommunication',
      label: 'Language & Communication',
      count: keyNeeds?.LanguageAndCommunication || 0,
    },
    { key: 'ADHD', label: 'ADHD', count: keyNeeds?.ADHD || 0 },
    { key: 'Dyslexia', label: 'Dyslexia', count: keyNeeds?.Dyslexia || 0 },
    { key: 'Other', label: 'Other', count: keyNeeds?.Other || 0 },
  ];
}

export default function SencoCommandCenter() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [yearGroup, setYearGroup] = useState('');
  const [needType, setNeedType] = useState('');
  const [gender, setGender] = useState('');
  const [interventionType, setInterventionType] = useState('');
  const [staffLead, setStaffLead] = useState('');
  const [daysAhead, setDaysAhead] = useState(30);
  const [actionFilter, setActionFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getSencoCommandCenterV2({
        yearGroups: yearGroup ? [yearGroup] : [],
        needTypes: needType ? [needType] : [],
        genders: gender ? [gender] : [],
        interventionTypes: interventionType ? [interventionType] : [],
        staffLeads: staffLead ? [staffLead] : [],
        daysAhead,
      });
      const next = response?.data?.commandCenter || null;
      setPayload(next);
    } catch (err) {
      const message = err?.message || 'Failed to load command center data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [yearGroup, needType, gender, interventionType, staffLead, daysAhead]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const needsRows = useMemo(() => needsSummaryRows(payload?.keyNeeds), [payload]);
  const topNeed = useMemo(() => {
    const ranked = needsRows
      .filter((row) => row.key !== 'Other' && row.count > 0)
      .sort((a, b) => b.count - a.count);
    if (ranked.length > 0) return ranked[0];
    const other = needsRows.find((row) => row.key === 'Other' && row.count > 0);
    return other || null;
  }, [needsRows]);
  const dueSoon = payload?.upcomingTargetReviews || [];
  const actionQueue = payload?.actionQueue || [];
  const topInterventions = payload?.topInterventions || [];
  const topStrategies = payload?.topUniversalStrategies || [];
  const trainingAreas = payload?.staffTrainingCoverage?.trainingAreas || {};
  const topTrainingAreas = Object.entries(trainingAreas)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  const filteredActions = useMemo(() => {
    const now = Date.now();
    if (actionFilter === 'all') return actionQueue;
    if (actionFilter === 'high') return actionQueue.filter((a) => a.priority === 'high');
    if (actionFilter === 'overdue') {
      return actionQueue.filter((a) => {
        if (a.status === 'overdue') return true;
        if (!a.dueDate) return false;
        const due = new Date(a.dueDate).getTime();
        return Number.isFinite(due) && due < now;
      });
    }
    if (actionFilter === 'week') {
      const in7 = now + 7 * 24 * 60 * 60 * 1000;
      return actionQueue.filter((a) => {
        if (!a.dueDate) return false;
        const due = new Date(a.dueDate).getTime();
        return Number.isFinite(due) && due >= now && due <= in7;
      });
    }
    return actionQueue;
  }, [actionQueue, actionFilter]);

  const exportCsv = () => {
    if (!payload) return;
    const rows = [
      ['metric', 'value'],
      ['cohortSize', String(payload.cohortSize || 0)],
      ['missingDataStudents', String(payload.missingDataStudents || 0)],
      ['tier1Students', String(payload.tier1Students || 0)],
      ['actionQueueCount', String(actionQueue.length)],
      [''],
      ['actionType', 'priority', 'status', 'studentName', 'dueDate', 'summary', 'route'],
      ...filteredActions.map((item) => [
        item.actionType || '',
        item.priority || '',
        item.status || '',
        item.studentName || '',
        item.dueDate || '',
        (item.summary || '').replace(/,/g, ';'),
        item.route || '',
      ]),
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `senco-command-center-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="rounded-2xl border border-gray-100 bg-white shadow-md">
      <div className="border-b border-gray-100 p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">SENCO Command Center</h2>
            <p className="mt-1 text-sm text-gray-600">
              Whole-school needs snapshot, urgent actions, provision trends, and staff capability.
            </p>
          </div>
          <button
            onClick={fetchData}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Icons.ArrowPath className="h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <label className="text-xs font-medium text-gray-600">
            Year group
            <input
              value={yearGroup}
              onChange={(e) => setYearGroup(e.target.value)}
              placeholder="e.g. Year 3"
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900"
            />
          </label>
          <label className="text-xs font-medium text-gray-600">
            Need type
            <select
              value={needType}
              onChange={(e) => setNeedType(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900"
            >
              <option value="">All needs</option>
              {NEED_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-medium text-gray-600">
            Gender
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900"
            >
              <option value="">All genders</option>
              {GENDER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-medium text-gray-600">
            Intervention type
            <input
              value={interventionType}
              onChange={(e) => setInterventionType(e.target.value)}
              placeholder="e.g. Speech & language"
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900"
            />
          </label>
          <label className="text-xs font-medium text-gray-600">
            Staff lead
            <input
              value={staffLead}
              onChange={(e) => setStaffLead(e.target.value)}
              placeholder="e.g. Ms Jones"
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900"
            />
          </label>
          <label className="text-xs font-medium text-gray-600">
            Due window (days)
            <input
              value={daysAhead}
              onChange={(e) => setDaysAhead(Number(e.target.value) || 30)}
              type="number"
              min={7}
              max={120}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900"
            />
          </label>
        </div>
      </div>

      <div className="p-5">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Icons.ArrowPath className="h-4 w-4 animate-spin" />
            Loading command center...
          </div>
        )}
        {!loading && error && (
          <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        {!loading && !error && payload && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div className="rounded-xl bg-blue-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Filtered cohort</p>
                <p className="mt-1 text-2xl font-bold text-blue-900">{payload.cohortSize || 0}</p>
              </div>
              <div className="rounded-xl bg-amber-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Data gaps</p>
                <p className="mt-1 text-2xl font-bold text-amber-900">{payload.missingDataStudents || 0}</p>
              </div>
              <div className="rounded-xl bg-emerald-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Tier 1 coverage</p>
                <p className="mt-1 text-2xl font-bold text-emerald-900">{payload.tier1Students || 0}</p>
              </div>
              <div className="rounded-xl bg-purple-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-purple-600">Top need</p>
                <p className="mt-1 text-lg font-bold text-purple-900">
                  {topNeed ? `${topNeed.label} (${topNeed.count})` : 'No data'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div className="rounded-xl border border-gray-100 p-4 lg:col-span-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-gray-900">SENCO Action Queue</h3>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'all', label: `All (${actionQueue.length})` },
                      { key: 'high', label: 'High priority' },
                      { key: 'overdue', label: 'Overdue' },
                      { key: 'week', label: 'Due in 7 days' },
                    ].map((chip) => (
                      <button
                        key={chip.key}
                        onClick={() => setActionFilter(chip.key)}
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          actionFilter === chip.key
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {chip.label}
                      </button>
                    ))}
                  </div>
                </div>
                {filteredActions.length === 0 ? (
                  <p className="mt-3 text-sm text-gray-500">No actions in this filter.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {filteredActions.slice(0, 12).map((item) => (
                      <li key={item.id} className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="font-medium text-gray-900">{item.studentName}</p>
                          <div className="flex items-center gap-2">
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                item.priority === 'high'
                                  ? 'bg-red-100 text-red-700'
                                  : item.priority === 'medium'
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {item.priority}
                            </span>
                            {item.dueDate && (
                              <span className="text-xs text-gray-500">
                                Due {new Date(item.dueDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="mt-1 text-gray-700">{item.summary}</p>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <p className="text-xs text-gray-500">{item.recommendedAction}</p>
                          <Link
                            to={item.route}
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-white"
                          >
                            Open
                            <Icons.ArrowTopRightOnSquare className="h-3.5 w-3.5" />
                          </Link>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-xl border border-gray-100 p-4">
                <h3 className="text-sm font-semibold text-gray-900">Key school needs</h3>
                <div className="mt-3 space-y-2">
                  {needsRows.map((item) => (
                    <div key={item.key} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{item.label}</span>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-800">
                        {item.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-gray-100 p-4">
                <h3 className="text-sm font-semibold text-gray-900">Upcoming target reviews (30 days)</h3>
                {dueSoon.length === 0 ? (
                  <p className="mt-3 text-sm text-gray-500">No reviews due in the selected window.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {dueSoon.slice(0, 6).map((item) => (
                      <li key={item.id} className="rounded-lg bg-gray-50 px-3 py-2 text-sm">
                        <p className="font-medium text-gray-900">{item.studentName}</p>
                        <p className="text-gray-600">{item.targetTitle}</p>
                        <p className="text-xs text-gray-500">
                          Review by {item.reviewDate ? new Date(item.reviewDate).toLocaleDateString() : 'date missing'}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              <div className="rounded-xl border border-gray-100 p-4">
                <h3 className="text-sm font-semibold text-gray-900">Universal offer signals</h3>
                {topStrategies.length === 0 ? (
                  <p className="mt-3 text-sm text-gray-500">No universal strategies captured yet.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {topStrategies.slice(0, 4).map((row) => (
                      <li key={row.strategy} className="text-sm text-gray-700">
                        <span className="font-medium">{row.count}x</span> {row.strategy}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-xl border border-gray-100 p-4">
                <h3 className="text-sm font-semibold text-gray-900">Intervention load</h3>
                {topInterventions.length === 0 ? (
                  <p className="mt-3 text-sm text-gray-500">No intervention assignment data in this filter.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {topInterventions.slice(0, 6).map((row) => (
                      <li key={row.interventionType} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{row.interventionType}</span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-800">
                          {row.count}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-xl border border-gray-100 p-4">
                <h3 className="text-sm font-semibold text-gray-900">Staff training capability</h3>
                <p className="mt-2 text-sm text-gray-600">
                  {payload.staffTrainingCoverage?.trainedStaffCount || 0} of{' '}
                  {payload.staffTrainingCoverage?.staffCount || 0} staff have tagged training records.
                </p>
                {topTrainingAreas.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {topTrainingAreas.map(([area, count]) => (
                      <li key={area} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{area}</span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-800">
                          {count}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={exportCsv}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Icons.ArrowDownTray className="h-4 w-4" />
                Export CSV
              </button>
              <Link
                to="/progress-dashboard"
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <Icons.ChartPie className="h-4 w-4" />
                Open progress dashboard
              </Link>
              <Link
                to="/interventions"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Icons.Target className="h-4 w-4" />
                Review interventions
              </Link>
              <Link
                to="/student-organization"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Icons.Users className="h-4 w-4" />
                Student-level drilldown
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
